import _ from "lodash"
import classNames from "classnames/bind"
import ProgressBar from "components/widgets/ProgressBar"
import PropTypes from "prop-types"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"
import "stylesheets/ThemeTabs.scss"
import { OBJECTIVES_THEMES } from "utils/constants/pillars"
import { useAppContext } from "../AppContext"
import { IMPROVE_MATURITY_PAGE } from "utils/constants/improve/pages"
import { useEntity } from "hooks/useAxios"
import EnvCollectActivatedThemeEntity from "services/entities/EnvCollectActivatedThemeEntity"
import ThemeTabActivator from "components/widgets/ThemeTabActivator"
import NewBadge from "components/widgets/NewBadge"

const ThemeTabs = ({
  pageName,
  pillar,
  objective,
  theme,
  progress,
  collect,
  disabledThemes = [],
  hiddenThemes = [],
}) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const themes = OBJECTIVES_THEMES[objective]
  const {
    subscription,
    reloadEnvCollect,
    currentRootNode,
    currentOrganization,
    currentNode,
  } = useAppContext()
  // Consolidated (parent) nodes hold no activation on their own collect; activation lives on the
  // children's collects. Use the subtree union to reflect activation at the consolidated level.
  const isConsolidated = currentNode?.has_children
  const activatedThemeList = isConsolidated
    ? collect?.activated_themes_in_subtree
    : collect?.activated_themes
  const activateThemeEntity = useEntity(EnvCollectActivatedThemeEntity)

  const toggleTheme = async (theme) => {
    const callback = collect?.activated_themes?.includes(theme)
      ? activateThemeEntity.deactivate
      : activateThemeEntity.activate

    await callback(theme)
    reloadEnvCollect()
  }

  const shouldShowCheckbox = () => {
    return (
      currentRootNode?.is_leaser ||
      currentRootNode?.is_provider ||
      currentOrganization?.is_partial_partner ||
      currentRootNode?.has_corporate_it_flexibility_subscription
    )
  }

  const shouldThemeBeActivated = (theme) => {
    if (currentRootNode?.is_leaser) {
      return activatedThemeList?.includes(theme)
    }

    if (
      currentRootNode?.is_provider ||
      currentOrganization?.is_partial_partner ||
      currentRootNode?.has_corporate_it_flexibility_subscription
    ) {
      return activatedThemeList?.includes(theme)
    }

    return true
  }

  const shouldShowProgressBar = (isThemeActivated) => {
    if (isThemeActivated) {
      return true
    }

    return (
      currentRootNode?.is_corporate &&
      !currentRootNode?.is_leaser &&
      !currentOrganization?.is_partial_partner &&
      !currentRootNode?.has_corporate_it_flexibility_subscription
    )
  }

  const themeListMarkup = useCallback(() => {
    if (themes === undefined || themes.count === 0) return null //first condition in case objective is undefined (when we want to be able to click on a pillar only), second condition in case objectivesThemes[objective] is an empty array

    const items = _.reject(themes, (existingTheme) => {
      if (hiddenThemes.includes(existingTheme)) return true
      switch (pageName) {
        case IMPROVE_MATURITY_PAGE:
          return (
            existingTheme === "positive_impact" || existingTheme === "cloud"
          )

        case "collect":
          return existingTheme === "cloud"
        case "collect/overview":
          return existingTheme === "positive_impact"
        default:
          return false
      }
    }).map((existingTheme, i) => {
      // All positive impact themes have the same themeId
      const customTheme =
        existingTheme === "positive_impact"
          ? `odd${objective}_${existingTheme}`
          : existingTheme

      const isActivated = shouldThemeBeActivated(customTheme)

      const showProgressBar = shouldShowProgressBar(isActivated)

      const showCheckbox = shouldShowCheckbox()

      const activatedThemes = collect?.env_collect_activated_themes

      const activatedTheme =
        activatedThemes &&
        activatedThemes.find(
          (editableTheme) => editableTheme.name === customTheme
        )

      const isEditable = isConsolidated
        ? false
        : !activatedTheme
        ? true
        : activatedTheme.is_editable
      const isDisabled = disabledThemes.includes(existingTheme)

      return (
        <li key={i}>
          <div
            className={classNames(pillar, "container", {
              selected: theme === existingTheme,
              activated: isActivated,
              disabled: isDisabled,
            })}
          >
            <Link
              to={
                isDisabled
                  ? "#"
                  : `/${pageName}/${pillar}/${objective}/${existingTheme}`
              }
              onClick={(e) => isDisabled && e.preventDefault()}
            >
              <button
                className={classNames(pillar, {
                  selected: theme === existingTheme,
                  activated: isActivated,
                  disabled: isDisabled,
                })}
                disabled={isDisabled}
              >
                {t(
                  `collect:pillars.${pillar}.objectives.${objective}.themes.${existingTheme}.title`
                )}
                {existingTheme === "ai_services" && (
                  <NewBadge
                    storageKey="collect_ai_services_new_badge"
                    currentPath={pathname}
                    seenOnPathPrefix={`/${pageName}/${pillar}/${objective}/ai_services`}
                  />
                )}
                {progress && showProgressBar && !isDisabled && (
                  <ProgressBar
                    progress={progress}
                    pillarId={pillar}
                    objectiveId={objective}
                    themeId={existingTheme}
                  />
                )}
              </button>
            </Link>
            {showCheckbox && !isDisabled && (
              <ThemeTabActivator
                isActivated={isActivated}
                toggle={() => toggleTheme(customTheme)}
                isEditable={isEditable}
              />
            )}
          </div>
        </li>
      )
    })
    return items
  }, [objective, theme, progress])

  return (
    <ul
      className={classNames(pillar, "ThemeTabs", {
        "--empty": themes === undefined,
      })}
    >
      {themeListMarkup()}
    </ul>
  )
}

ThemeTabs.propTypes = {
  pageName: PropTypes.string.isRequired,
  pillar: PropTypes.string.isRequired,
  objective: PropTypes.string,
  theme: PropTypes.string,
  progress: PropTypes.object,
  collect: PropTypes.object,
  disabledThemes: PropTypes.arrayOf(PropTypes.string),
  hiddenThemes: PropTypes.arrayOf(PropTypes.string),
}

export default ThemeTabs
